import React from 'react';
import { ArrowRight, BookOpen, Gift, Check } from 'lucide-react';
import { SITE } from '../../mock';

const CHAPTERS = [
  '00  You Were Never Meant to Chase Wealth',
  '01  Reprogramming the Money Mind',
  '02  Ending Fear & Doubt',
  '03  Karma, Energy & Wealth',
  '04  Installing the Wealth Identity',
  '05  Aligned Action',
  '06  Wealth Flow & Stability',
  '07  Multiple Income Streams',
  '08  The Shadow Principles',
];

const BONUSES = [
  {
    title: 'The Gratitude Journal',
    sub: 'Daily prompts to rewire scarcity into abundance',
    desc:
      'A guided journal with daily prompts engineered to shift your nervous system from lack to gratitude — the fastest known channel to abundance.',
  },
  {
    title: "Ho'oponopono Vision Board",
    sub: 'Ancient Hawaiian clearing meets modern manifestation',
    desc:
      "A printable vision board template combining the 4-phrase Ho'oponopono clearing ritual with focused visualisation for deep subconscious healing.",
  },
  {
    title: 'HRCM Vision Board',
    sub: 'Health · Relationships · Career · Money',
    desc:
      'A holistic vision board template covering the four pillars of life. Map your destination across every domain — so nothing leaks.',
  },
  {
    title: 'Love Letter to Money',
    sub: 'Heal your relationship with money in 20 minutes',
    desc:
      'A guided writing exercise to repair the emotional rupture most people unknowingly carry with money — instantly shifting how it flows to you.',
  },
  {
    title: 'Money Manifestation Vision Board',
    sub: 'Anchor your wealth identity visually, daily',
    desc:
      'A money-focused vision board template designed to imprint specific financial outcomes into your subconscious through repeated focused attention.',
  },
  {
    title: 'Theta Visualisation Audio',
    sub: 'Realise your dreams faster — in your theta brainwave state',
    desc:
      'A guided audio that drops your brain into the suggestible theta state. Use it twice a day: the last thing before sleep and the first thing after waking.',
  },
];

const PRICE_STACK = [
  { label: 'You Are The Magician (eBook)', value: '₹499' },
  { label: 'The Gratitude Journal', value: '₹2,000' },
  { label: "Ho'oponopono Vision Board", value: '₹2,000' },
  { label: 'HRCM Vision Board', value: '₹2,000' },
  { label: 'Love Letter to Money', value: '₹2,000' },
  { label: 'Money Manifestation Vision Board', value: '₹2,000' },
  { label: 'Theta Visualisation Audio', value: '₹2,000' },
];

export default function BookBonuses({ paymentLink }) {
  return (
    <>
      {/* About the book */}
      <section className="relative py-20 md:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 md:gap-16 items-center">
          <div>
            <p className="text-[11px] tracking-[0.25em] uppercase text-amber-300/80 mb-5">
              About The Book
            </p>
            <h2 className="font-serif text-white text-[34px] md:text-[52px] leading-[1.05] tracking-tight">
              A system, not a sermon.
            </h2>
            <p className="mt-6 text-neutral-400 text-[15px] md:text-[17px] leading-relaxed">
              Most books tell you to “think positive.” This one shows you how the mind is wired,
              where the leaks are, and how to rebuild the vessel that holds wealth.
            </p>
            <p className="mt-4 text-neutral-400 text-[15px] md:text-[17px] leading-relaxed">
              Each chapter pairs a principle with an actionable assignment. You don't just read —
              you apply, observe, and shift.
            </p>
            <p className="mt-4 text-amber-200 font-serif text-[18px] md:text-[20px] italic">
              By the final page you won't be waiting for abundance. You'll be the one generating
              it.
            </p>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-amber-300/15 via-transparent to-transparent blur-2xl" />
            <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-navy-900">
              <img
                src={SITE.promoArt}
                alt="You Are The Magician — promo art"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Table of contents */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-[11px] tracking-[0.25em] uppercase text-amber-300/80 mb-5 text-center">
            Table of Contents
          </p>
          <h2 className="font-serif text-center text-white text-[32px] md:text-[48px] leading-[1.05] tracking-tight">
            Nine chapters.
            <br />
            <span className="italic text-amber-200">One transformation.</span>
          </h2>

          <div className="mt-12 rounded-2xl border border-white/10 bg-navy-900/40 overflow-hidden">
            {CHAPTERS.map((c, i) => (
              <div
                key={c}
                className={`flex items-center gap-4 px-6 py-4 md:px-8 md:py-5 ${
                  i < CHAPTERS.length - 1 ? 'border-b border-white/5' : ''
                }`}
              >
                <BookOpen size={18} className="text-amber-300 shrink-0" strokeWidth={1.5} />
                <p className="font-serif text-white text-[16px] md:text-[19px] tracking-tight whitespace-pre">
                  {c}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bonuses */}
      <section id="bonuses" className="relative py-20 md:py-28 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-[11px] tracking-[0.25em] uppercase text-amber-300/80 mb-5 text-center">
            When You Order Today
          </p>
          <h2 className="font-serif text-center text-white text-[32px] md:text-[48px] leading-[1.05] tracking-tight">
            You don't just get a book.
            <br />
            <span className="italic text-amber-200">You get the entire toolkit.</span>
          </h2>
          <p className="mt-5 text-center text-neutral-400 text-[15px] md:text-[16px] leading-relaxed max-w-2xl mx-auto">
            Six free bonuses — journals, vision boards, a money-healing ritual, and a
            brainwave-shifting audio — so you don't just <em>read</em> the book. You actually live
            it.
          </p>
          <p className="mt-3 text-center text-amber-300/90 text-[12px] tracking-[0.2em] uppercase">
            Total bonus value: ₹12,000 · FREE with your order
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {BONUSES.map((b, i) => (
              <div
                key={b.title}
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-navy-900/50 p-6 md:p-7 transition-all hover:border-amber-300/30 hover:-translate-y-[2px]"
              >
                <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-amber-300/[0.05] blur-3xl group-hover:bg-amber-300/[0.12] transition-colors" />
                <div className="relative">
                  <div className="flex items-center justify-between gap-2">
                    <span className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-amber-300/90">
                      <Gift size={12} /> Bonus #{i + 1}
                    </span>
                    <span className="text-[11px] tracking-[0.18em] uppercase">
                      <span className="text-neutral-500 line-through mr-1.5">₹2,000</span>
                      <span className="text-amber-300">Free</span>
                    </span>
                  </div>
                  <h3 className="mt-4 font-serif text-white text-[22px] md:text-[24px] tracking-tight">
                    {b.title}
                  </h3>
                  <p className="mt-1 text-amber-200/90 text-[13px]">{b.sub}</p>
                  <p className="mt-3 text-neutral-400 text-[13.5px] leading-relaxed">{b.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Price stack */}
          <div className="mt-16 max-w-2xl mx-auto rounded-2xl border border-white/10 bg-navy-900/50 p-7 md:p-9">
            <ul className="space-y-3">
              {PRICE_STACK.map((p) => (
                <li key={p.label} className="flex items-center justify-between text-sm md:text-base">
                  <span className="inline-flex items-center gap-2 text-neutral-300">
                    <Check size={14} className="text-amber-300" /> {p.label}
                  </span>
                  <span className="text-neutral-500 line-through">{p.value}</span>
                </li>
              ))}
            </ul>
            <div className="mt-5 pt-5 border-t border-white/10 flex items-center justify-between">
              <span className="text-[12px] tracking-[0.2em] uppercase text-neutral-500">
                Total Value
              </span>
              <span className="font-serif text-neutral-500 text-2xl line-through">₹12,499</span>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <span className="text-[12px] tracking-[0.2em] uppercase text-amber-300">
                You Pay Today
              </span>
              <span className="font-serif bg-gradient-to-b from-amber-100 to-amber-400 bg-clip-text text-transparent text-[40px] md:text-[48px] leading-none">
                ₹399
              </span>
            </div>
            <a
              href={paymentLink}
              target="_blank"
              rel="noreferrer"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full px-7 py-4 text-[15px] font-semibold text-navy-900 bg-gradient-to-b from-amber-200 to-amber-400 shadow-[0_10px_36px_-6px_rgba(251,191,36,0.55)] hover:-translate-y-[1px] hover:shadow-[0_16px_44px_-6px_rgba(251,191,36,0.75)] transition-all"
            >
              Claim Book + 6 Bonuses — ₹399
              <ArrowRight size={16} />
            </a>
            <p className="mt-3 text-center text-[11px] tracking-[0.18em] uppercase text-neutral-500">
              Everything delivered to your email instantly
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
